import type { APIDesignerStore } from '..';
import type { MIMETypeValue } from '../BodySchemaEditor/utils';
import type { $TSFixMe } from '@readme/iso';
import type { HttpMethods } from 'oas/types';
import type { OpenAPIV3 } from 'openapi-types';
import type { StateCreator } from 'zustand';

import { codes } from '@readme/http-status-codes';
import Oas from 'oas';

import { actionLog } from '@core/store/util';

import { isTextBasedMIMEType } from '../BodySchemaEditor/utils';

interface AugmentedResponseExample {
  /**
   * Should be a concatenation of the status code and the summary (i.e., `namedId`).
   *
   * If the summary doesn't exist (e.g., if a single example is documented using the `example` object),
   * it will just be the status code.
   *
   * @example '200_example1'
   * @example '200'
   */
  id: string;

  /**
   * The media type of the example.
   *
   * @example 'application/json'
   */
  mediaType: string;

  /**
   * The summary (e.g., the example key in the `examples` object`) of the example.
   *
   * If the example is documented using the `example` object, this will be `undefined`.
   *
   * @example 'example1'
   */
  namedId?: string;

  /**
   * The status code of the example.
   *
   * @example '200'
   */
  status: string;
}

export interface ResponseExampleEditorSliceState {
  /**
   * The last edited response example. Instead of using selectedExample, we use this to track the last edited example.
   * This is useful for triggering UI re-renders and may be useful to track changes
   */
  lastEditedExample: AugmentedResponseExample | null;
}
export interface MediaTypeExample {
  description?: string;
  summary?: string;
  title?: string;
  value: unknown;
}

export interface ResponseExampleEditorSliceActions {
  /**
   * Adds a custom response example to the current operation.
   */
  addResponseExample: (statusCode: string, specificMimeType: string) => void;

  /**
   * Changes the content type of the currently selected example.
   */
  changeContentType: (newContentType: string, oldContentType: string, statusCode: string, namedId: string) => void;

  /**
   * Changes the status code of the currently selected example.
   */
  changeStatusCode: (newStatusCode: string, oldStatusCode: string, mediaType: string, namedId: string) => void;

  /**
   * Deletes the currently selected example.
   */
  deleteResponseExample: (statusCode: string, mediaType: string, namedId: string) => void;

  /**
   * Returns a map of status codes to media types to example names.
   * Format: Map<STATUS_CODE, Map<MEDIA_TYPE, EXAMPLE_NAMES>>
   *
   */
  getAllExampleNames: () => Map<string, Map<string, string[]>>;

  /**
   * Returns all the status codes that have actual response schemas
   */
  getAllValidStatusCodes: () => string[];

  /**
   * Returns the code of the example with the given status code, media type, and named ID.
   */
  getExampleCode: (statusCode: string, mediaType: string, namedId: string) => string;

  /**
   * Returns the first example in the operation.
   */
  getFirstExample: () => AugmentedResponseExample;

  /**
   * Returns the response example properties on the operation object.
   */
  getOperationResponseProperties: (
    contentType: string,
    statusCode: string,
  ) => {
    hasContentType: boolean;
    hasCustomDescription: boolean;
    hasExamplesObject: boolean;
    hasMultipleContentTypes: boolean;
    hasMultipleExamples: boolean;
    hasResponseObject: boolean;
    hasStatusCode: boolean;
  };

  initialize: () => void;

  /**
   * Updates the name of the currently selected example.
   */
  updateResponseExampleName: (exampleName: string, statusCode: string, mediaType: string, namedId: string) => void;

  /**
   * Updates the code of the currently selected example.
   */
  updateSnippet: (value: string, statusCode: string, mediaType: string, namedId: string) => void;
}

export interface ResponseExampleEditorSlice {
  /**
   * State slice containing fields and actions that pertain to the Response Example Editor
   */
  responseExampleEditor: ResponseExampleEditorSliceActions & ResponseExampleEditorSliceState;
}

export const initialState: ResponseExampleEditorSliceState = {
  lastEditedExample: null,
};

/**
 * Response Example Editor state slice containing fields related to the Manual Editor — Response Example Editor.
 */
export const createResponseExampleEditorSlice: StateCreator<
  APIDesignerStore & ResponseExampleEditorSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  ResponseExampleEditorSlice
> = (set, get) => ({
  responseExampleEditor: {
    ...initialState,

    /**
     * Add a new response example to an existing status code and MIME type.
     * Since we already have both, we only need to handle adding examples to existing content.
     *
     */
    addResponseExample: (statusCode: string, specificMimeType: string) => {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();
      const newOperation = structuredClone(operation);

      // https://spec.openapis.org/oas/v3.1.0#response-object
      const currentResponseObject = operation?.responses?.[statusCode] as OpenAPIV3.ResponseObject | undefined;

      // Ensuring the example name is unique
      const baseExampleLabel = 'New Example';
      let newExampleCount = 1;
      let newExampleLabel = baseExampleLabel;
      const currentExampleLabels = Object.keys(currentResponseObject?.content?.[specificMimeType]?.examples || {});

      while (currentExampleLabels.includes(newExampleLabel)) {
        newExampleLabel = `${baseExampleLabel} ${newExampleCount}`;
        newExampleCount += 1;
      }

      const newExampleValue = {
        summary: newExampleLabel,
        value: '{}',
      };

      const hasExampleObject = !!currentResponseObject?.content?.[specificMimeType]?.example;
      const hasExamplesObject = !!currentResponseObject?.content?.[specificMimeType]?.examples;

      // Handle the case where we need to convert from 'example' to 'examples'
      if (hasExampleObject && !hasExamplesObject) {
        const oldExample = currentResponseObject?.content?.[specificMimeType]?.example;

        newOperation!.responses![statusCode] = {
          content: {
            [specificMimeType]: {
              examples: {
                [`exampleFromExisting${statusCode}`]: {
                  value: oldExample,
                },
                [newExampleLabel]: newExampleValue,
              },
            },
          },
          description: currentResponseObject?.description || codes[statusCode][0],
        };

        delete (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject)?.content?.[specificMimeType]?.example;
      } else {
        // Add to existing examples or create examples object
        const response = newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject;
        if (!response.content) {
          response.content = {};
        }
        if (!response.content[specificMimeType]) {
          response.content[specificMimeType] = {};
        }
        if (!response.content[specificMimeType].examples) {
          response.content[specificMimeType].examples = {};
        }

        response.content[specificMimeType].examples![newExampleLabel] = newExampleValue;
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.responseExampleEditor.lastEditedExample = {
            id: `${statusCode}_${newExampleLabel}`,
            mediaType: specificMimeType,
            namedId: newExampleLabel,
            status: statusCode,
          };
        },
        false,
        actionLog('adding response example'),
      );
    },

    changeContentType: (newContentType: string, oldContentType: string, statusCode: string, namedId: string) => {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();
      const newOperation = structuredClone(operation);

      // find the example in the old content type under content.[oldContentType].examples
      const response = newOperation?.responses?.[statusCode] as OpenAPIV3.ResponseObject;
      const oldExamples = response?.content?.[oldContentType]?.examples || {};
      const exampleNames = Object.keys(oldExamples);
      const exampleIndex = exampleNames.findIndex(exampleName => exampleName === namedId);

      if (exampleIndex === -1) {
        // Create an example with existing values if not found
        const existingValue = response?.content?.[oldContentType]?.example || '';
        if (!response?.content?.[newContentType]?.examples) {
          (response?.content?.[newContentType] as OpenAPIV3.MediaTypeObject).examples = {};
        }
        (response?.content?.[newContentType] as OpenAPIV3.MediaTypeObject).examples![namedId] = {
          summary: namedId,
          value: existingValue,
        };
      } else {
        // move the example to the new content type
        const exampleToMove = oldExamples[namedId];
        delete oldExamples[namedId];
        if (!response?.content?.[newContentType]?.examples) {
          (response?.content?.[newContentType] as OpenAPIV3.MediaTypeObject).examples = {};
        }
        (response?.content?.[newContentType] as OpenAPIV3.MediaTypeObject).examples![namedId] = exampleToMove;

        // clean up empty examples objects only
        if (Object.keys(oldExamples).length === 0) {
          delete response?.content?.[oldContentType]?.examples;
        }
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.responseExampleEditor.lastEditedExample = {
            id: `${statusCode}_${namedId}`,
            mediaType: newContentType,
            namedId,
            status: statusCode,
          };
        },
        false,
        actionLog('changeContentType'),
      );
    },

    changeStatusCode: (newStatusCode: string, oldStatusCode: string, mediaType: string, namedId: string) => {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();
      const newOperation = structuredClone(operation);

      // find the example in the old status code under content.[mediaType].examples
      const oldResponse = newOperation?.responses?.[oldStatusCode] as OpenAPIV3.ResponseObject;
      const oldExamples = oldResponse?.content?.[mediaType]?.examples || {};
      const exampleNames = Object.keys(oldExamples);
      const exampleIndex = exampleNames.findIndex(exampleName => exampleName === namedId);

      // check if target status code exists
      if (!newOperation?.responses?.[newStatusCode]) {
        throw new Error(`Target status code '${newStatusCode}' does not exist.`);
      }

      const newResponse = newOperation!.responses![newStatusCode] as OpenAPIV3.ResponseObject;
      const newContentType = Object.keys(newResponse.content || {})[0];

      if (exampleIndex === -1) {
        // basically this is the default example for text based media types
        // text based responses are given a default example if they have an example prop but not examples prop
        // as soon as users interact with the editor, the example is moved to the examples object
        // in this case, we simply create a new example with the same name and value as the default example
        const existingExamples = newResponse.content![newContentType]!.examples || {};
        newResponse.content![newContentType]!.examples = {
          ...existingExamples,
          [namedId]: {
            summary: namedId,
            value: oldResponse.content![mediaType]!.example || '{}',
          },
        };
      }

      if (!newResponse?.content?.[mediaType]) {
        /**
         * Cases:
         * 1. From an application content type to a text based content type
         * 2. From a text based content type to an application content type
         * 3. From an application content type to an application content type
         * 4. From a text based content type to a text based content type
         *
         * For all cases, we persist the examples value ONLY, we do not persist any value from the example property
         * This is because the example property is used to store the default example for text based media types
         * and we want to only persist the custom examples
         */
        // case 1
        if (!isTextBasedMIMEType(mediaType as MIMETypeValue) && isTextBasedMIMEType(newContentType as MIMETypeValue)) {
          const existingExamples = newResponse.content![newContentType]!.examples || {};
          newResponse.content![newContentType]!.examples = {
            ...existingExamples,
            [namedId]: {
              summary: namedId,
              value: (oldResponse.content![mediaType]!.examples![namedId] as OpenAPIV3.ExampleObject)?.value || '{}',
            },
          };
          delete oldResponse.content![mediaType]!.examples![namedId];
        }
        // case 2
        else if (
          isTextBasedMIMEType(mediaType as MIMETypeValue) &&
          !isTextBasedMIMEType(newContentType as MIMETypeValue)
        ) {
          const existingExamples = newResponse.content![newContentType]!.examples || {};
          const exampleToMove = oldResponse.content?.[mediaType]?.examples?.[namedId] as OpenAPIV3.ExampleObject;
          if (exampleToMove) {
            newResponse.content![newContentType]!.examples = {
              ...existingExamples,
              [namedId]: {
                summary: namedId,
                value: (oldResponse.content![mediaType]!.examples![namedId] as OpenAPIV3.ExampleObject)?.value || '{}',
              },
            };
            delete oldResponse.content![mediaType]!.examples![namedId];
          }
        }
        // case 3
        else if (
          !isTextBasedMIMEType(mediaType as MIMETypeValue) &&
          !isTextBasedMIMEType(newContentType as MIMETypeValue)
        ) {
          const existingExamples = newResponse.content![newContentType]!.examples || {};
          newResponse.content![newContentType]!.examples = {
            ...existingExamples,
            [namedId]: oldResponse.content![mediaType]!.examples![namedId],
          };
          delete oldResponse.content![mediaType]!.examples![namedId];
        }
        // case 4
        else if (
          isTextBasedMIMEType(mediaType as MIMETypeValue) &&
          isTextBasedMIMEType(newContentType as MIMETypeValue)
        ) {
          const existingExamples = newResponse.content![newContentType]!.examples || {};
          const exampleToMove = oldResponse.content?.[mediaType]?.examples?.[namedId] as OpenAPIV3.ExampleObject;
          if (exampleToMove) {
            newResponse.content![newContentType]!.examples = {
              ...existingExamples,
              [namedId]: exampleToMove,
            };
            delete oldResponse.content![mediaType]!.examples![namedId];
          }
        }
      } else {
        // move the example to the new status code
        const exampleToMove = oldExamples[namedId];
        delete oldExamples[namedId];
        if (!newResponse.content![mediaType].examples) {
          newResponse.content![mediaType].examples = {};
        }
        newResponse.content![mediaType].examples![namedId] = exampleToMove;
      }

      // clean up empty examples objects only
      if (Object.keys(oldExamples).length === 0) {
        delete oldResponse?.content?.[mediaType]?.examples;
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.responseExampleEditor.lastEditedExample = {
            id: `${newStatusCode}_${namedId}`,
            mediaType,
            namedId,
            status: newStatusCode,
          };
        },
        false,
        actionLog('changeStatusCode'),
      );
    },

    deleteResponseExample: (statusCode: string, mediaType: string, namedId: string) => {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();
      const newOperation = structuredClone(operation);

      // Only delete the specific example, never delete media types or response objects
      const responseObject = newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject;
      if (responseObject?.content?.[mediaType]?.examples) {
        delete responseObject.content![mediaType]!.examples![namedId as string];

        // If this was the last example, delete the entire examples property
        if (Object.keys(responseObject.content![mediaType]!.examples!).length === 0) {
          delete responseObject.content![mediaType]!.examples;
        }
      } else if (responseObject?.content?.[mediaType]?.example) {
        delete responseObject.content![mediaType]!.example;
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.responseExampleEditor.lastEditedExample = {
            id: `${statusCode}_${namedId}`,
            mediaType,
            namedId,
            status: statusCode,
          };
        },
        false,
        actionLog('deleteResponseExample'),
      );

      set(() => {}, false, actionLog('setting selected response example to first available example'));
    },

    getAllExampleNames: () => {
      const schema = get().apiObject?.schema;
      const currentPath = get().apiObject?.path as string;
      const currentMethod = get().apiObject?.method as HttpMethods;

      if (!schema || !currentPath || !currentMethod) {
        throw new Error('Required API object properties are missing');
      }

      // Create Oas instance to use its methods
      const oas = new Oas(schema);
      const operation = oas.operation(currentPath, currentMethod);
      const examples = operation.getResponseExamples();
      const statusMap = new Map<string, Map<string, string[]>>();

      examples.forEach(example => {
        const mediaTypeMap = new Map<string, string[]>();

        Object.entries(example.mediaTypes).forEach(([mediaType, mediaTypeExamples]) => {
          if (isTextBasedMIMEType(mediaType as MIMETypeValue)) {
            if (mediaTypeExamples && mediaTypeExamples.length > 0) {
              const exampleNames = mediaTypeExamples
                .map(ex => ex.title || ex.summary) // fallback to summary if title is not present
                .filter((name): name is string => Boolean(name));
              mediaTypeMap.set(
                mediaType,
                exampleNames.length > 0 ? exampleNames : [`${example.status} - ${mediaType} - Default`],
              );
            } else {
              mediaTypeMap.set(mediaType, [`${example.status} - ${mediaType} - Default`]);
            }
          } else {
            const exampleNames = mediaTypeExamples
              .map(ex => ex.title || ex.summary) // fallback to summary if title is not present
              .filter((name): name is string => Boolean(name));

            mediaTypeMap.set(mediaType, exampleNames);
          }
        });

        statusMap.set(example.status, mediaTypeMap);
      });

      return statusMap;
    },

    getAllValidStatusCodes: () => {
      const { operation } = get().getCurrentOperation();
      const statusCodes = Object.keys(operation?.responses || {});

      return statusCodes.filter(statusCode => {
        const responseObject = operation?.responses?.[statusCode] as OpenAPIV3.ResponseObject | undefined;
        return responseObject?.content && Object.keys(responseObject.content).length > 0;
      });
    },

    getFirstExample: () => {
      const { currentPath, currentMethod } = get().getCurrentOperation();

      const oas = new Oas(get().apiObject?.schema || '{}');
      const operation = oas.operation(currentPath, currentMethod);

      const firstRawExample = operation?.getResponseExamples()[0];
      const firstStatus = firstRawExample?.status || '';
      const firstMediaType = firstRawExample ? Object.keys(firstRawExample.mediaTypes)[0] : '';
      const firstNamedId = firstMediaType ? firstRawExample.mediaTypes[firstMediaType][0]?.summary : '';

      return {
        id: firstNamedId ? firstStatus.concat('_', firstNamedId) : firstStatus,
        mediaType: firstMediaType,
        namedId: firstNamedId,
        status: firstStatus,
      };
    },

    getOperationResponseProperties: (contentType: string, statusCode: string) => {
      const { operation } = get().getCurrentOperation();

      // https://spec.openapis.org/oas/v3.1.0#responses-object
      const hasResponseObject = !!operation?.responses || false;

      // https://spec.openapis.org/oas/v3.1.0#response-object
      const responseObject = operation?.responses?.[statusCode] as OpenAPIV3.ResponseObject | undefined;

      const hasStatusCode = !!responseObject || false;
      const hasContentType = !!responseObject?.content?.[contentType] || false;
      const hasCustomDescription = responseObject?.description !== codes[statusCode][0];
      const hasMultipleContentTypes = Object.keys(responseObject?.content || {}).length > 1;

      // https://spec.openapis.org/oas/v3.1.0#media-type-object
      const mediaTypeObject = responseObject?.content?.[contentType];

      const hasExamplesObject = !!mediaTypeObject?.examples || false;
      const hasMultipleExamples = Object.keys(mediaTypeObject?.examples || {}).length > 1;

      return {
        hasResponseObject,
        hasStatusCode,
        hasContentType,
        hasCustomDescription,
        hasMultipleContentTypes,
        hasExamplesObject,
        hasMultipleExamples,
      };
    },

    getExampleCode: (statusCode: string, mediaType: string, namedId: string) => {
      const responseObject = get().getCurrentOperation()?.operation?.responses?.[
        statusCode
      ] as OpenAPIV3.ResponseObject;

      // First try to get from examples object
      const exampleFromExamples = responseObject?.content?.[mediaType]?.examples?.[
        namedId as string
      ] as OpenAPIV3.ExampleObject;
      if (exampleFromExamples?.value) {
        return exampleFromExamples.value;
      }

      // If no examples object or no value, try schema.example for text-based media types
      if (isTextBasedMIMEType(mediaType as MIMETypeValue)) {
        const schemaExample = (responseObject?.content?.[mediaType]?.schema as OpenAPIV3.SchemaObject)?.example;
        if (schemaExample) {
          return schemaExample;
        }
      }

      // Return empty string if no example found
      return '';
    },

    initialize: () => {
      set(
        state => {
          state.responseExampleEditor.lastEditedExample = state.responseExampleEditor.getFirstExample();
          return state;
        },
        false,
        actionLog('initialize response example editor slice'),
      );
    },

    updateResponseExampleName(exampleName: string, statusCode: string, mediaType: string, namedId: string) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const newOperation = structuredClone(operation);

      if (!newOperation!.responses![statusCode]) {
        // Should never get here, but making ts happy
        throw new Error('Cannot find selected example in response object.');
      }

      // Casting the response object to an OpenAPIV3.ResponseObject since we know it's not a $ref
      const responseObject = newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject;

      // For text-based media types, ensure examples object exists
      if (isTextBasedMIMEType(mediaType as MIMETypeValue) && !responseObject.content![mediaType]?.examples) {
        if (!responseObject.content![mediaType]) {
          responseObject.content![mediaType] = {};
        }
        responseObject.content![mediaType].examples = {};

        // Create a default example with the namedId if it doesn't exist
        if (!responseObject.content![mediaType].examples![namedId]) {
          // Try to get the original value from schema.example or example property
          let originalValue = '';
          const schemaExample = (responseObject.content![mediaType].schema as OpenAPIV3.SchemaObject)?.example;
          const singleExample = responseObject.content![mediaType].example;

          if (schemaExample !== undefined) {
            originalValue = schemaExample;
          } else if (singleExample !== undefined) {
            originalValue = singleExample;
          }

          responseObject.content![mediaType].examples![namedId] = {
            summary: namedId,
            value: originalValue,
          };
        }
      }

      if (!responseObject.content![mediaType] || !namedId || !responseObject.content![mediaType]!.examples![namedId]) {
        // Should never get here, but making ts happy
        throw new Error('Cannot find selected example in response object.');
      }

      // Make sure the example doesn't overwrite any existing ones
      // If there is already an example with this name, we'll append a number to the end of the name until we find a unique name
      let newExampleName = exampleName;
      let exampleIndex = 1;
      while (responseObject.content![mediaType]!.examples![newExampleName]) {
        newExampleName = `${exampleName} ${exampleIndex}`;
        exampleIndex += 1;
      }

      // Casting the example object to an OpenAPIV3.ExampleObject since we know it's not a $ref
      const newExample = responseObject.content![mediaType]!.examples![namedId] as OpenAPIV3.ExampleObject;
      newExample.summary = newExampleName;
      responseObject.content![mediaType]!.examples![newExampleName] = newExample;

      delete responseObject.content![mediaType]!.examples![namedId];

      newOperation!.responses![statusCode] = responseObject;
      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.responseExampleEditor.lastEditedExample = {
            id: `${statusCode}_${newExampleName}`,
            mediaType,
            namedId: newExampleName,
            status: statusCode,
          };
        },
        false,
        actionLog('updateResponseExampleName'),
      );
    },

    updateSnippet(value, statusCode, mediaType, namedId) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();
      const newOperation = structuredClone(operation);

      let currentValue: $TSFixMe = value;

      if (mediaType === 'application/json') {
        try {
          currentValue = JSON.parse(value);
        } catch (e) {
          /* empty */
        }
      }

      // https://spec.openapis.org/oas/v3.1.0#response-object
      const responseObject = operation!.responses![statusCode] as OpenAPIV3.ResponseObject;

      const hasExamplesObject = !!responseObject?.content?.[mediaType]?.examples;
      const hasExampleObject = !!responseObject?.content?.[mediaType]?.example;

      if (isTextBasedMIMEType(mediaType as MIMETypeValue) && !hasExamplesObject) {
        if (!(newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType]) {
          (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType] = {};
        }
        (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].examples = {};
      }

      let code;
      if (hasExamplesObject) {
        code = (responseObject?.content?.[mediaType]?.examples?.[namedId as string] as OpenAPIV3.ExampleObject)?.value;
      } else if (hasExampleObject && !isTextBasedMIMEType(mediaType as MIMETypeValue)) {
        code = responseObject?.content?.[mediaType]?.example;
      }

      // note: we must check manually for undefined and null because the code can be an empty string, doing `if (code)` will essentially make empty examples uneditable
      if (code !== undefined && code !== null) {
        if (hasExamplesObject) {
          (
            (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].examples![
              namedId as string
            ] as OpenAPIV3.ExampleObject
          ).value = currentValue;
        } else if (hasExampleObject && !isTextBasedMIMEType(mediaType as MIMETypeValue)) {
          (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].example = currentValue;
        }
      }

      if (isTextBasedMIMEType(mediaType as MIMETypeValue)) {
        if (
          !(newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].examples![
            namedId as string
          ]
        ) {
          (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].examples![
            namedId as string
          ] = {
            summary: namedId,
            value: currentValue,
          };
        } else {
          (
            (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].examples![
              namedId as string
            ] as OpenAPIV3.ExampleObject
          ).value = currentValue;
        }
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.responseExampleEditor.lastEditedExample = {
            id: `${statusCode}_${namedId}`,
            mediaType,
            namedId,
            status: statusCode,
          };
        },
        false,
        actionLog('updateSnippet', { value, statusCode, mediaType, namedId }),
      );
    },
  },
});
